import { Component, inject, signal, computed, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

import {
  GlobalMasterService,
  GlobalMaster,
  GlobalMasterDto,
  GlobalType,
  GlobalTypeDto,
  ActiveType,
} from '../../services/global-master';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';

/**
 * Global Master — master/detail admin screen: parent data types (GlobalMaster) on the left,
 * the selected type's child values (GlobalTypeMaster) on the right. The two relate by value
 * (GlobalType.dataType === GlobalMaster.dataTypeName), so the value dialog's data-type select is
 * sourced from the active parents and locked to the selected type — never free-typed.
 */
@Component({
  selector: 'app-global-master',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DialogModule,
    SelectModule,
    TableModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    InputTextModule,
    InputNumberModule,
    ToggleSwitchModule,
  ],
  templateUrl: './global-master.html',
  styleUrls: ['./global-master.css'],
})
export class GlobalMasterPage implements OnInit {
  private fb = inject(FormBuilder);
  private svc = inject(GlobalMasterService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);
  readonly pageTitle = inject(PageTitleService).title;

  // ----- data types (left) -----
  loadingTypes = signal(false);
  types = signal<GlobalMaster[]>([]);
  selected = signal<GlobalMaster | null>(null);

  // ----- values (right) -----
  loadingValues = signal(false);
  values = signal<GlobalType[]>([]);

  // ----- dialogs -----
  showTypeModal = signal(false);
  editingType = signal(false);
  savingType = signal(false);

  showValueModal = signal(false);
  editingValue = signal(false);
  savingValue = signal(false);
  activeTypes = signal<ActiveType[]>([]);

  selectedName = computed(() => this.selected()?.dataTypeName ?? '');

  typeForm: FormGroup = this.fb.group({
    id: [0],
    dataTypeName: ['', Validators.required],
    displayName: [''],
    description: [''],
    sortOrder: [0],
    isActive: [true],
  });

  valueForm: FormGroup = this.fb.group({
    id: [0],
    dataType: ['', Validators.required],
    dataValue: ['', Validators.required],
    displayOrder: [0],
    isActive: [true],
  });

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.loadTypes();
  }

  // ===================== data types =====================
  loadTypes() {
    this.loadingTypes.set(true);
    this.svc.gmAll().subscribe({
      next: (res) => {
        this.loadingTypes.set(false);
        const list = res?.data ?? [];
        this.types.set(list);
        // Keep the current selection across a refresh (its valueCount may have changed); else
        // fall back to the first row so the right panel always has something to show.
        const cur = this.selected();
        const keep = cur ? list.find((t) => t.id === cur.id) : undefined;
        if (keep) this.selected.set(keep);
        else if (list.length) this.selectType(list[0]);
        else {
          this.selected.set(null);
          this.values.set([]);
        }
      },
      error: () => this.loadingTypes.set(false),
    });
  }

  selectType(t: GlobalMaster) {
    this.selected.set(t);
    this.loadValues(t.dataTypeName);
  }

  isSelected(t: GlobalMaster): boolean {
    return this.selected()?.id === t.id;
  }

  openAddType() {
    this.editingType.set(false);
    const nextOrder = (this.types().at(-1)?.sortOrder ?? 0) + 1;
    this.typeForm.reset({
      id: 0,
      dataTypeName: '',
      displayName: '',
      description: '',
      sortOrder: nextOrder,
      isActive: true,
    });
    this.showTypeModal.set(true);
  }

  openEditType(t: GlobalMaster) {
    this.editingType.set(true);
    this.typeForm.reset({
      id: t.id,
      dataTypeName: t.dataTypeName,
      displayName: t.displayName ?? '',
      description: t.description ?? '',
      sortOrder: t.sortOrder ?? 0,
      isActive: t.isActive ?? true,
    });
    this.showTypeModal.set(true);
  }

  closeTypeModal() {
    this.showTypeModal.set(false);
  }

  saveType() {
    if (this.typeForm.invalid) {
      this.typeForm.markAllAsTouched();
      this.toast.warning('Data type name is required.');
      return;
    }
    const v = this.typeForm.getRawValue();
    const dto: GlobalMasterDto = {
      id: this.editingType() ? Number(v.id) : 0,
      dataTypeName: (v.dataTypeName || '').trim(),
      displayName: (v.displayName || '').trim() || null,
      description: (v.description || '').trim() || null,
      sortOrder: Number(v.sortOrder) || 0,
      isActive: !!v.isActive,
    };
    const req = this.editingType() ? this.svc.gmUpdate(dto) : this.svc.gmInsert(dto);
    this.savingType.set(true);
    req.subscribe({
      next: (res: any) => {
        this.savingType.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to save data type.');
          return;
        }
        this.toast.success(res?.message || (this.editingType() ? 'Data type updated' : 'Data type created'));
        // Only when the EDITED type is the selected one: sync its name and reload its (possibly
        // renamed) values — a rename cascades to the children. Editing an unrelated type needs no
        // value refetch.
        if (this.editingType() && this.selected()?.id === dto.id) {
          this.selected.update((s) => (s ? { ...s, dataTypeName: dto.dataTypeName } : s));
          this.loadValues(dto.dataTypeName);
        }
        this.closeTypeModal();
        this.loadTypes();
      },
      error: () => this.savingType.set(false),
    });
  }

  toggleType(t: GlobalMaster) {
    const next = !t.isActive;
    this.patchType(t.id, { isActive: next });
    const req = next ? this.svc.gmEnable(t.id) : this.svc.gmDisable(t.id);
    req.subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.patchType(t.id, { isActive: t.isActive });
          this.toast.error(res?.message || 'Could not update status.');
        }
      },
      error: () => {
        this.patchType(t.id, { isActive: t.isActive });
        this.toast.error('Could not update status.');
      },
    });
  }

  private patchType(id: number, patch: Partial<GlobalMaster>) {
    this.types.update((list) => list.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    if (this.selected()?.id === id) this.selected.update((s) => (s ? { ...s, ...patch } : s));
  }

  async removeType(t: GlobalMaster) {
    if (t.valueCount > 0) {
      this.toast.warning(`Disable instead — "${t.dataTypeName}" still has ${t.valueCount} value(s).`);
      return;
    }
    const ok = await this.toast.confirm(`"${t.dataTypeName}" will be deleted.`, 'Delete data type?', 'Delete');
    if (!ok) return;
    this.svc.gmDelete(t.id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          // 409 — backend blocks delete while children exist; surface its message verbatim.
          this.toast.error(res?.message || 'Failed to delete data type.');
          return;
        }
        this.toast.success(res?.message || 'Data type deleted');
        if (this.selected()?.id === t.id) this.selected.set(null);
        this.loadTypes();
      },
    });
  }

  // ===================== values =====================
  loadValues(dataType: string) {
    this.loadingValues.set(true);
    this.svc.gtByType(dataType).subscribe({
      next: (res) => {
        this.loadingValues.set(false);
        this.values.set(res?.data ?? []);
      },
      error: () => this.loadingValues.set(false),
    });
  }

  private ensureActiveTypes() {
    if (this.activeTypes().length) return;
    this.svc.gmActive().subscribe({ next: (res) => this.activeTypes.set(res?.data ?? []) });
  }

  openAddValue() {
    const sel = this.selected();
    if (!sel) return;
    this.ensureActiveTypes();
    this.editingValue.set(false);
    const nextOrder = (this.values().at(-1)?.displayOrder ?? 0) + 1;
    this.valueForm.reset({
      id: 0,
      dataType: sel.dataTypeName,
      dataValue: '',
      displayOrder: nextOrder,
      isActive: true,
    });
    this.valueForm.get('dataType')!.disable(); // scoped + locked to the selected type
    this.showValueModal.set(true);
  }

  openEditValue(r: GlobalType) {
    this.ensureActiveTypes();
    this.editingValue.set(true);
    this.valueForm.reset({
      id: r.id,
      dataType: r.dataType,
      dataValue: r.dataValue,
      displayOrder: r.displayOrder ?? 0,
      isActive: r.isActive ?? true,
    });
    this.valueForm.get('dataType')!.disable();
    this.showValueModal.set(true);
  }

  closeValueModal() {
    this.showValueModal.set(false);
  }

  saveValue() {
    if (this.valueForm.invalid) {
      this.valueForm.markAllAsTouched();
      this.toast.warning('Value is required.');
      return;
    }
    const v = this.valueForm.getRawValue(); // getRawValue() includes the locked dataType
    const dto: GlobalTypeDto = {
      id: this.editingValue() ? Number(v.id) : 0,
      dataType: (v.dataType || '').trim(),
      dataValue: (v.dataValue || '').trim(),
      displayOrder: Number(v.displayOrder) || 0,
      isActive: !!v.isActive,
    };
    const req = this.editingValue() ? this.svc.gtUpdate(dto) : this.svc.gtInsert(dto);
    this.savingValue.set(true);
    req.subscribe({
      next: (res: any) => {
        this.savingValue.set(false);
        if (res?.success === false) {
          // 404 (inactive/missing parent) or 409 (duplicate value) — surface verbatim.
          this.toast.error(res?.message || 'Failed to save value.');
          return;
        }
        this.toast.success(res?.message || (this.editingValue() ? 'Value updated' : 'Value created'));
        this.closeValueModal();
        this.loadValues(dto.dataType);
        this.loadTypes(); // refresh the parent's valueCount
      },
      error: () => this.savingValue.set(false),
    });
  }

  toggleValue(r: GlobalType) {
    const next = !r.isActive;
    this.patchValue(r.id, { isActive: next });
    const req = next ? this.svc.gtEnable(r.id) : this.svc.gtDisable(r.id);
    req.subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.patchValue(r.id, { isActive: r.isActive });
          this.toast.error(res?.message || 'Could not update status.');
        }
      },
      error: () => {
        this.patchValue(r.id, { isActive: r.isActive });
        this.toast.error('Could not update status.');
      },
    });
  }

  private patchValue(id: number, patch: Partial<GlobalType>) {
    this.values.update((list) => list.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  }

  async removeValue(r: GlobalType) {
    const ok = await this.toast.confirm(`"${r.dataValue}" will be deleted.`, 'Delete value?', 'Delete');
    if (!ok) return;
    this.svc.gtDelete(r.id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to delete value.');
          return;
        }
        this.toast.success(res?.message || 'Value deleted');
        this.loadValues(r.dataType);
        this.loadTypes();
      },
    });
  }
}
